import { Box, CircularProgress } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import ViewPickerInput from "../shared/ViewPickerInput";
import { useV2Theme } from "../../hooks/useV2Theme";
import {
  useUpdateInteraction,
  useUpdateInteractionTags,
  useUpdateInteractionViews,
  useInteractionTags,
} from "../../../hooks/useInteraction";
import type Interaction from "../../../types/interaction/Interaction";
import { getRetentionDate } from "../../../types/interaction/Interaction";
import {
  flyoutInputSx,
  flyoutLabelSx,
  tabSx,
  closeButtonSx,
  neutralChipRemoveBtnSx,
  inlineDrawerSx,
  drawerHeaderSx,
  drawerTitleSx,
  drawerTabBarSx,
  drawerScrollAreaSx,
} from "../shared/crudStyles";
import type { TabId } from "../shared/crudStyles";
import { computeTagSuggestions } from "../../helpers/tagSuggestions";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a unix-seconds timestamp to a datetime-local input value string */
function epochToDatetimeLocal(epoch: number | null): string {
  if (!epoch) return "";
  const d = new Date(epoch * 1000);
  // datetime-local needs YYYY-MM-DDTHH:mm format in local time
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EditInteractionDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly interaction: Interaction | null;
  readonly customerId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditInteractionDrawer({
  open,
  onClose,
  interaction,
  customerId,
}: EditInteractionDrawerProps) {
  const { t } = useTranslation(["common", "search"]);
  const { mode } = useV2Theme();
  const { user } = useContext(AuthContext);

  // -- Permission & feature flag checks ------------------------------------
  const canEdit = user?.hasPermission("interactions:Update") ?? false;
  const hasViews = user?.hasFeatureFlag("Views") ?? false;

  // -- Tab state -----------------------------------------------------------
  const [activeTab, setActiveTab] = useState<TabId>("retention");

  // -- Retention form state ------------------------------------------------
  const [retentionDate, setRetentionDate] = useState("");
  const [removeRetention, setRemoveRetention] = useState(false);
  const [legalHold, setLegalHold] = useState(false);

  // -- Tags form state -----------------------------------------------------
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [tagInputFocused, setTagInputFocused] = useState(false);

  // -- Views form state ----------------------------------------------------
  const [views, setViews] = useState<string[]>([]);

  // -- Available tags for autocomplete -------------------------------------
  const { data: availableTags } = useInteractionTags(customerId);

  // -- API -----------------------------------------------------------------
  const updateMutation = useUpdateInteraction();
  const updateTagsMutation = useUpdateInteractionTags();
  const updateViewsMutation = useUpdateInteractionViews();
  const isPending =
    updateMutation.isPending ||
    updateTagsMutation.isPending ||
    updateViewsMutation.isPending;

  // -- Populate form when interaction changes ------------------------------
  useEffect(() => {
    if (interaction) {
      setRetentionDate(epochToDatetimeLocal(getRetentionDate(interaction)));
      setRemoveRetention(false);
      setLegalHold(interaction.legalHold);
      setTags([...(interaction.tags ?? [])]);
      setViews([...(interaction.views ?? [])]);
      setTagInput("");
      setActiveTab("retention");
    }
  }, [interaction]);

  // -- Derived: track what changed -----------------------------------------
  const originalRetentionStr = interaction
    ? epochToDatetimeLocal(getRetentionDate(interaction))
    : "";
  const originalLegalHold = interaction?.legalHold ?? false;
  const originalTags = interaction?.tags ?? [];
  const originalViews = interaction?.views ?? [];

  const retentionChanged = removeRetention
    ? originalRetentionStr !== ""
    : retentionDate !== originalRetentionStr;
  const legalHoldChanged = legalHold !== originalLegalHold;
  const tagsChanged =
    tags.length !== originalTags.length ||
    !tags.every((t) => originalTags.includes(t));
  const viewsChanged =
    views.length !== originalViews.length ||
    !views.every((v) => originalViews.includes(v));

  const hasChanges =
    retentionChanged || legalHoldChanged || tagsChanged || viewsChanged;

  const confirmDisabled = !hasChanges || !canEdit || isPending;

  // -- Derived: min date for datetime-local --------------------------------
  const todayStr = new Date().toISOString().slice(0, 16);

  // -- Tag autocomplete filtering ------------------------------------------
  const filteredTagSuggestions = computeTagSuggestions({
    availableTags,
    currentTags: tags,
    inputValue: tagInput,
    isFocused: tagInputFocused,
  });

  // -- Submit --------------------------------------------------------------
  const handleSubmit = async () => { // NOSONAR
    if (!interaction) return;

    try {
      // 1. Retention / Legal Hold — only send changed fields
      if (retentionChanged || legalHoldChanged) {
        const payload: {
          customerId: string;
          interactionId: string;
          newRetentionDate?: number | null;
          newLegalHold?: boolean;
        } = {
          customerId,
          interactionId: interaction.interactionId,
        };

        if (legalHoldChanged) {
          payload.newLegalHold = legalHold;
        }

        // Only send retention date if the user explicitly changed it
        // AND legal hold isn't being toggled on (API handles the migration)
        // Use 0 to clear (API sentinel for "remove retention date")
        if (retentionChanged && !legalHold) {
          const retentionTimestamp = retentionDate
            ? Math.floor(new Date(retentionDate).getTime() / 1000)
            : 0;
          payload.newRetentionDate = removeRetention ? 0 : retentionTimestamp;
        }

        await updateMutation.mutateAsync(payload);
      }

      // 2. Tags — send full replacement if changed
      if (tagsChanged) {
        await updateTagsMutation.mutateAsync({
          customerId,
          interactionId: interaction.interactionId,
          newTags: tags,
        });
      }

      // 3. Views — send full replacement if changed
      if (viewsChanged) {
        await updateViewsMutation.mutateAsync({
          customerId,
          interactionId: interaction.interactionId,
          newViews: views,
        });
      }

      handleClose();
    } catch (error) {
      console.error("Failed to update interaction:", error);
    }
  };

  // -- Reset on close ------------------------------------------------------
  const handleClose = () => {
    setActiveTab("retention");
    setRetentionDate("");
    setRemoveRetention(false);
    setLegalHold(false);
    setTags([]);
    setViews([]);
    setTagInput("");
    setTagInputFocused(false);
    updateMutation.reset();
    updateTagsMutation.reset();
    updateViewsMutation.reset();
    onClose();
  };

  // -- Tag helpers ---------------------------------------------------------
  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!open) return null;

  return (
    <Box sx={inlineDrawerSx("editDrawerSlideIn")}>
        {/* ---- Header ---- */}
        <Box sx={drawerHeaderSx}>
          <Box sx={drawerTitleSx}>
            {t("common:editInteractionTitle")}
          </Box>
          <Box component="button" onClick={handleClose} sx={closeButtonSx}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 16, height: 16 }}>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Box>
        </Box>

        {/* ---- Tabs ---- */}
        <Box sx={drawerTabBarSx}>
          <Box component="button" onClick={() => setActiveTab("retention")} sx={tabSx(activeTab === "retention")}>
            {t("common:retention")}
          </Box>
          <Box component="button" onClick={() => setActiveTab("tags")} sx={tabSx(activeTab === "tags")}>
            {t("common:tags")}
          </Box>
          {hasViews && (
            <Box component="button" onClick={() => setActiveTab("views")} sx={tabSx(activeTab === "views")}>
              {t("common:views")}
            </Box>
          )}
        </Box>

        {/* ---- Scrollable Content ---- */}
        <Box sx={drawerScrollAreaSx}>
          {/* ---- Retention Tab ---- */}
          {activeTab === "retention" && (
            <>
              <Box>
                <Box sx={flyoutLabelSx}>{t("common:retentionDate")}</Box>
                <Box
                  component="input"
                  type="datetime-local"
                  value={retentionDate}
                  min={todayStr}
                  disabled={legalHold || removeRetention}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRetentionDate(e.target.value)}
                  sx={{
                    ...flyoutInputSx,
                    colorScheme: mode === "dark" ? "dark" : "light",
                    opacity: (legalHold || removeRetention) ? 0.4 : 1,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: legalHold ? "default" : "pointer",
                  userSelect: "none",
                  opacity: legalHold ? 0.4 : 1,
                }}
                onClick={() => {
                  if (legalHold) return;
                  const next = !removeRetention;
                  setRemoveRetention(next);
                  if (next) {
                    setRetentionDate("");
                  } else {
                    setRetentionDate(originalRetentionStr);
                  }
                }}
              >
                <Box
                  component="input"
                  type="checkbox"
                  checked={removeRetention}
                  disabled={legalHold}
                  onChange={() => {
                    if (legalHold) return;
                    const next = !removeRetention;
                    setRemoveRetention(next);
                    if (next) {
                      setRetentionDate("");
                    } else {
                      setRetentionDate(originalRetentionStr);
                    }
                  }}
                  sx={{ accentColor: "var(--accent)", width: 16, height: 16, cursor: legalHold ? "default" : "pointer" }}
                />
                <Box sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
                  {t("common:removeRetentionDate")}
                </Box>
              </Box>

              {legalHold && (
                <Box
                  sx={{
                    fontSize: "0.8125rem",
                    color: "var(--text-tertiary)",
                    fontStyle: "italic",
                    mt: "-8px",
                  }}
                >
                  {t("common:retentionDateDisabledByHold")}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => setLegalHold(!legalHold)}
              >
                <Box
                  component="input"
                  type="checkbox"
                  checked={legalHold}
                  onChange={() => setLegalHold(!legalHold)}
                  sx={{ accentColor: "var(--accent)", width: 16, height: 16, cursor: "pointer" }}
                />
                <Box sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
                  {t("common:legalHold")}
                </Box>
              </Box>
            </>
          )}

          {/* ---- Tags Tab ---- */}
          {activeTab === "tags" && (
            <>
              <Box>
                <Box sx={flyoutLabelSx}>{t("common:tags")}</Box>
                <Box sx={{ position: "relative" }}>
                  <Box
                    component="input"
                    type="text"
                    name="ck-tag-input"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    inputMode="text"
                    // Block password managers from offering to fill / save this field.
                    data-1p-ignore=""
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    placeholder={t("common:typeTagPressEnter")}
                    value={tagInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagInput(e.target.value)}
                    onFocus={() => setTagInputFocused(true)}
                    onBlur={() => setTagInputFocused(false)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      } else if (e.key === "Escape") {
                        setTagInputFocused(false);
                      }
                    }}
                    sx={flyoutInputSx}
                  />
                  {/* Autocomplete suggestions */}
                  {filteredTagSuggestions.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        mt: "4px",
                        backgroundColor: "var(--bg-raised)",
                        border: "1px solid var(--border-mid)",
                        borderRadius: "8px",
                        maxHeight: "160px",
                        overflowY: "auto",
                        zIndex: 10,
                      }}
                    >
                      {filteredTagSuggestions.map((suggestion) => (
                        <Box
                          key={suggestion}
                          // onMouseDown + preventDefault so the input doesn't blur
                          // before the suggestion is added (which would hide the dropdown).
                          onMouseDown={(e: React.MouseEvent) => {
                            e.preventDefault();
                            if (!tags.includes(suggestion)) {
                              setTags([...tags, suggestion]);
                            }
                            setTagInput("");
                          }}
                          sx={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: "0.8125rem",
                            "&:hover": { backgroundColor: "var(--bg-hover)" },
                          }}
                        >
                          {suggestion}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Current tags as chips */}
              {tags.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {tags.map((tag) => (
                    <Box
                      key={tag}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        backgroundColor: "var(--bg-hover)",
                        fontSize: "0.75rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      {tag}
                      <Box
                        component="button"
                        onClick={() => removeTag(tag)}
                        sx={neutralChipRemoveBtnSx}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ width: 10, height: 10 }}>
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}

          {/* ---- Views Tab ---- */}
          {activeTab === "views" && hasViews && (
            <Box>
              <Box sx={flyoutLabelSx}>{t("common:views")}</Box>
              <ViewPickerInput
                customerId={customerId}
                selectedViewIds={views}
                onChange={setViews}
                chipVariant="neutral"
              />
            </Box>
          )}
        </Box>

        {/* ---- Footer ---- */}
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            padding: "16px 20px",
            borderTop: "1px solid var(--border-subtle)",
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            onClick={handleClose}
            sx={{
              flex: 1,
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid var(--border-mid)",
              backgroundColor: "transparent",
              color: "var(--text-secondary)",
              fontSize: "0.8125rem",
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
              "&:hover": { backgroundColor: "var(--bg-hover)" },
            }}
          >
            {t("common:cancel")}
          </Box>
          <Box
            component="button"
            onClick={handleSubmit}
            disabled={confirmDisabled}
            sx={{
              flex: 1,
              padding: "10px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: confirmDisabled
                ? "var(--bg-hover)"
                : "var(--accent)",
              color: confirmDisabled
                ? "var(--text-tertiary)"
                : "#fff",
              fontSize: "0.8125rem",
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: confirmDisabled ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              "&:hover": {
                backgroundColor: confirmDisabled
                  ? "var(--bg-hover)"
                  : "var(--accent-hover)",
              },
            }}
          >
            {isPending && <CircularProgress size={14} sx={{ color: "inherit" }} />}
            {t("common:save")}
          </Box>
        </Box>
    </Box>
  );
}
